import React, { useState, cloneElement, createElement, type JSX } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { twJoin } from "tailwind-merge";
import type { Placement } from "@popperjs/core";

import { SupBadgeLabel } from "../../../components/UI/Badge/Label";
import { usePortalRoot } from "../../../hooks/usePortalRoot";
import { CommonColors } from "../../../types/colors";

import { TooltipPopper } from "./components/TooltipPopper";

interface ContentRendererProps {
  arrowPlacement: "top" | "bottom" | "left" | "right";
}

export interface TooltipProps {
  className?: string;
  popperClassName?: string;
  popperElClassName?: string;
  as?: string;
  placement?: Placement;
  color: "default" | CommonColors;
  content: string | JSX.Element;
  children: JSX.Element;
  offset?: number;
  portal?: boolean;
  unmount?: boolean;
  wrapper?: boolean;
}

const ARROW_COLOR = {
  default: "text-neutral-200",
  primary: "text-neutral-800",
  success: "text-success-600",
  error: "text-error-600",
  info: "text-info-500",
  warning: "text-warning-700",
} as const;

const ARROW_STYLES = {
  bottom: {
    transform: "rotate(0deg)",
    bottom: 0,
  },
  top: {
    transform: "rotate(180deg)",
    top: 0,
  },
  left: {
    transform: "rotate(90deg)",
    left: 0,
  },
  right: {
    transform: "rotate(-90deg)",
    right: 0,
  },
} as const;

const CONTAINER_STYLES = {
  bottom: "pb-1.5",
  top: "pt-1.5",
  left: "pl-1.5",
  right: "pr-1.5",
} as const;

const CONTAINER_SMALL_STYLES = {
  bottom: "pb-1",
  top: "pt-1",
  left: "pl-1",
  right: "pr-1",
} as const;

export function SupTooltip({
  className,
  popperClassName,
  popperElClassName,
  as = "span",
  wrapper = false,
  content,
  children,
  placement = "top",
  offset = 0,
  portal = false,
  unmount = false,
  color = "primary",
}: TooltipProps) {
  const [visible, setVisibility] = useState(false);
  const [elementRef, setElementRef] = useState<Element | null>(null);
  const [tooltipRef, setTooltipRef] = useState<HTMLElement | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);

  const popperMounted = visible || !unmount;
  const rootRef = usePortalRoot(portal && popperMounted);
  const { styles, state } = usePopper(elementRef, tooltipRef, {
    placement,
    modifiers: [
      { name: "offset", options: { offset: [0, offset] } },
      {
        name: "arrow",
        options: {
          element: arrowRef,
        },
      },
    ],
  });

  const getContentProps = (poperState: typeof state): ContentRendererProps => {
    let arrowPlacement: ContentRendererProps["arrowPlacement"] = "top";

    if (poperState?.placement.startsWith("bottom")) {
      arrowPlacement = "top";
    } else if (poperState?.placement.startsWith("top")) {
      arrowPlacement = "bottom";
    } else if (poperState?.placement.startsWith("right")) {
      arrowPlacement = "left";
    } else if (poperState?.placement.startsWith("left")) {
      arrowPlacement = "right";
    }
    return {
      arrowPlacement,
    };
  };

  const { arrowPlacement } = getContentProps(state);
  const small = typeof content === "string";

  const popper = (
    <span
      ref={setTooltipRef}
      style={styles.popper}
      className={twJoin(
        "sup-tooltip-popper inline-flex z-40",
        small ? CONTAINER_SMALL_STYLES[arrowPlacement] : CONTAINER_STYLES[arrowPlacement],
        visible ? "visible" : "invisible",
        popperClassName
      )}
    >
      {small ? (
        <SupBadgeLabel contentClassName={popperElClassName} hideArrow arrowPlacement={arrowPlacement} content={content} color={color} />
      ) : (
        <TooltipPopper className={popperElClassName} content={content} color={color} />
      )}

      <span
        ref={setArrowRef}
        className="inline-flex"
        style={{ ...styles.arrow, ...ARROW_STYLES[arrowPlacement], transform: [styles.arrow?.transform as string, ARROW_STYLES[arrowPlacement].transform].join(" ") }}
      >
        <svg className={twJoin("", ARROW_COLOR[color])} xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none">
          <path d="M4 6L0 0L8 0L4 6Z" fill="currentColor" />
        </svg>
      </span>
    </span>
  );

  const handleMouseEnter = () => {
    setVisibility(true);
  };

  const handleMouseLeave = () => {
    setVisibility(false);
  };

  if (wrapper) {
    return createElement(
      as,
      { ref: setElementRef, className: twJoin("sup-tooltip", !portal && "relative", className), onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
      children,
      portal ? rootRef && createPortal(popper, rootRef) : popperMounted && popper
    );
  }

  return createElement(
    React.Fragment,
    null,
    cloneElement(children, {
      ref: setElementRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    }),
    portal ? rootRef && createPortal(popper, rootRef) : popperMounted && popper
  );
}
